import { type HostConfig, type HostEnv, hostConfigOf } from '@tasks/bridge-client/host';

/**
 * TASKS-APP-FULL-UI-1 U2b — HOW this document relates to Vitto, decided ONCE from facts the page cannot forge:
 *
 *   embedded    framed by Vitto: the iframe, `?embedded=true`, and a `parentOrigin` that is EXACTLY one of the deploy's allowed parents.
 *               No own login, no persisted session (see `TokenStore`): every load asks the host for a pass.
 *   standalone  a deploy that knows its Vitto (`VITE_VITTO_ORIGIN`) but is the top window: the own login, and «Continue with Vitto».
 *   own         no Vitto configured (a plain build, a unit test): the product exactly as it was, password login only.
 *
 * A framed document whose parent is NOT allowed is `own`, not embedded: it never talks to that parent (the frame-ancestors header of
 * the deploy already keeps strangers from framing it; this is the second wall).
 */
export type HostMode =
  | { kind: 'own' }
  | { kind: 'standalone'; config: HostConfig }
  | { kind: 'embedded'; config: HostConfig; parentOrigin: string };

type Doc = { parent: unknown; location: { search: string } };

export function detectMode(win: Window | Doc, env: HostEnv, slug: string): HostMode {
  const config = hostConfigOf(env, slug);
  if (config === null) return { kind: 'own' };
  const framed = win.parent !== win;
  if (!framed) return { kind: 'standalone', config };
  const params = new URLSearchParams(win.location.search);
  const parentOrigin = params.get('embedded') === 'true' ? (params.get('parentOrigin') ?? '') : '';
  if (parentOrigin !== '' && config.parentOrigins.includes(parentOrigin))
    return { kind: 'embedded', config, parentOrigin };
  return { kind: 'own' };
}

/** The slug of the app (`apps/tasks/app.json#slug`, injected at build by vite.config): the name Vitto knows it by. */
export const appSlug = (): string =>
  typeof __APP_SLUG__ === 'string' && __APP_SLUG__ !== '' ? __APP_SLUG__ : 'tasks-next';

/** The mode of THIS document, decided at load (the session singleton is built from it). */
export const hostMode: HostMode = detectMode(
  globalThis.window,
  import.meta.env as HostEnv,
  appSlug(),
);
